import { useQueryClient } from '@tanstack/react-query';

/**
 * A reusable hook for updating or inserting an entity into
 * multiple paginated queries that share a common key prefix.
 *
 * @param {string | unknown[]} queryPrefix - The prefix used to match queries.
 *   For example: 'message_groups' or ['message_groups'].
 * @param {object} [options]
 * @param {Function} [options.findPageFn] - A function that checks if a given query
 *   is considered the "first page" where a new record can be inserted if not found elsewhere.
 * @returns A function that takes the new entity and updates the cache accordingly.
 *
 * Example:
 *   const updateEntity = usePaginatedEntityUpdater('message_groups', {
 *     findPageFn: (queryKey) => {
 *       // parse queryKey and decide if it's page=1, etc
 *     }
 *   });
 *
 *   updateEntity({
 *     id: 3,
 *     userId: 'some-uuid',
 *     name: 'wutang'
 *   });
 */
export function useEntityQueryUpdate({ findPageFn } = {}) {
    const queryClient = useQueryClient();

    if (!findPageFn) {
        findPageFn = findFirstPage;
    }

    const updateEntity = (
        queryPrefix,
        newEntity,
        selector = DEFAULT_SELECTOR,
        dataTransform = null,
        insert = false
    ) => {
        let foundEntity = false;

        // 1. Get all matching queries by prefix
        const queriesData = queryClient.getQueriesData({
            queryKey: queryPrefix,
        });

        // 2. Define updater function that replaces or adds the entity
        const addOrReplaceEntity = (newModel) => (oldData) => {
            const update = (entity) => {
                if (selector(entity, newModel)) {
                    foundEntity = true;
                    return newModel;
                }
                return entity;
            };

            if (!oldData) {
                return oldData;
            }

            if (oldData.pagination && Array.isArray(oldData.data)) {
                return {
                    data: oldData.data.map(update),
                    pagination: oldData.pagination,
                };
            }

            return Array.isArray(oldData)
                ? oldData.map(update)
                : update(oldData);
        };

        // 3. For each query, update data (this replaces the entity if present)
        queriesData.forEach(([key, oldData]) => {
            if (oldData != null) {
                queryClient.setQueryData(key, addOrReplaceEntity(newEntity));
            }
        });

        // 4. If the entity wasn't found in any of the queries,
        //    we optionally insert it into the "first page" or a target page.
        if (!foundEntity && findPageFn && insert) {
            // Find a query which meets the "first page" criteria (or any custom condition).
            const firstPageQuery = queriesData.find(([key]) => findPageFn(key));
            if (firstPageQuery) {
                const [firstPageKey] = firstPageQuery;
                queryClient.setQueryData(firstPageKey, (oldData) => {
                    const unexpectedStructure =
                        !oldData ||
                        !oldData.pagination ||
                        !Array.isArray(oldData.data);

                    if (unexpectedStructure) {
                        // If data doesn't match the expected structure, handle gracefully
                        return oldData;
                    }

                    return {
                        ...oldData,
                        data: [newEntity, ...oldData.data],
                    };
                });
            }
        }

        if (!foundEntity && Array.isArray(queryPrefix)) {
            queryClient.setQueryData(
                queryPrefix,
                addOrReplaceEntity(newEntity)
            );
        }
    };

    return function updateEntities(queryPrefixes, newEntity, insert = true) {
        if (!Array.isArray(queryPrefixes)) {
            updateEntity(
                [queryPrefixes.toString()],
                newEntity,
                DEFAULT_SELECTOR,
                null,
                insert
            );
            return;
        }

        if (!Array.isArray(queryPrefixes[0])) {
            updateEntity(
                queryPrefixes.map((x) => x.toString()),
                newEntity,
                DEFAULT_SELECTOR,
                null,
                insert
            );
            return;
        }

        queryPrefixes.forEach((queryPrefix) => {
            updateEntity(
                queryPrefix.map((x) => x.toString()),
                newEntity,
                DEFAULT_SELECTOR,
                null,
                insert
            );
        });
    };
}

const DEFAULT_SELECTOR = (entity, newEntity) => entity.id == newEntity.id;

const findFirstPage = (queryKey) => {
    // query key is something like ['message_groups', '{ "page":1, "limit":20 }']
    // parse the second item (JSON) and check if page=1
    if (!(Array.isArray(queryKey) && queryKey[1])) {
        return false;
    }

    try {
        const isJson = queryKey[1].startsWith('{');
        if (!isJson) {
            return false;
        }

        const pagination = JSON.parse(queryKey[1]);
        return pagination?.page == 1;
    } catch (e) {
        return false;
    }
};
